import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Debug from 'debug'
import { Base64 } from 'js-base64'
import { observer } from 'mobx-react-lite'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { useFileSearch } from '@jotta/grpc-connect-client/file/useFileSearch'
import {
  convertToLegacyItem,
  parsePathItem,
} from '@jotta/grpc-js-client/files/parsePathItem'
import { ErrorPage } from '@jotta/ui/ErrorPage'

import { DisplayActiveFileActionDialog } from '../FileActions/DisplayActiveFileActionDialog'
import { FileBrowserHeader } from '../FileBrowser/FileBrowserHeader'
import { FileCarousel } from '../FileCarousel/FileCarousel'
import { useFileStore } from '../FileStore/FileContext'
import { getFolderStore } from '../Folder/FolderStore'
import { FolderVirtualList } from '../Folder/FolderVirtualList'
import type { PathItemStore } from '../PathItem/PathItemStore'

import { NoFilesResults } from './NoFilesResults'

const debug = Debug('jotta:searchroute')

export const FileSearchRoute = observer(function FileSearchRoute() {
  const fileStore = useFileStore()
  const { query = '', '*': filehash = '' } = useParams()
  const [folder] = useState(() =>
    getFolderStore('/search', '/search', fileStore),
  )

  let file: PathItemStore | undefined = undefined

  try {
    if (filehash) {
      file = folder.getItemByPath(Base64.decode(filehash))
    }
  } catch (error) {
    debug(error)
  }

  const { isSuccess, isPending, data, isError, refetch, error } =
    useFileSearch(query)

  // transfer state to folder store
  useEffect(() => {
    if (!data) {
      return
    }
    const items = data.map(match =>
      parsePathItem({
        pathItem: convertToLegacyItem(match),
        ignoreChildren: true,
        ignoreDownloadLinks: false,
        isSearchResult: true,
      }),
    )
    folder.setItems(items)
  }, [data, folder])

  return (
    <div className="px-3">
      <FileBrowserHeader store={folder} />

      {isError && data?.length && (
        <div
          role="alert"
          className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900"
        >
          <Trans>Some results may be missing due to a timeout.</Trans>
          <button className="ml-2 underline" onClick={() => refetch()}>
            <Trans>Retry</Trans>
          </button>
        </div>
      )}
      {isSuccess && data.length === 0 ? (
        <NoFilesResults data-testid="empty-no-results" />
      ) : isError && !data?.length ? (
        <ErrorPage
          data-testid="empty-error"
          error={error}
          hideFooter
          title={t`Search failed`}
        />
      ) : (
        <>
          <FolderVirtualList store={folder} loading={isPending} />
          <DisplayActiveFileActionDialog store={folder} />
          <FileCarousel
            open={!!filehash}
            loading={isPending}
            file={file}
            files={folder.carouselPathItems}
          />
        </>
      )}
    </div>
  )
})
